import { Button, Divider, Stack, styled, Typography } from '@mui/material';
import { useSetMobileHeader } from 'features/navigation';
import { submitStatusSelector } from 'pages/candidateOffer/store';
import { Offer } from 'pages/candidateOffer/types';
import { DeclineOfferDialog, OfferDescription, SubmitDeclineCallback } from 'pages/candidateOfferList/components';
import AcceptOfferDialog from 'pages/candidateOfferList/containers/AcceptOfferDialog';
import useAcceptOfferDialogState from 'pages/candidateOfferList/hooks/useAcceptOfferDialogState';
import { closeDeclineDialog, declineOfferStateSelector, declineOfferThunk, openDeclineDialog } from 'pages/candidateOfferList/store';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ActionButtonsGroup, ButtonBackToList, FullPageContentCard, PageContentHeading, RoleSpecialtyChips, RoleSpecialtyOption, StickyBottomActionContainer } from 'shared/components';
import { APP_ROUTES } from 'shared/constants';
import { useTenantContext } from 'shared/contexts';
import { useAppDispatch, useFeatureEnabled, useIsMobile } from 'shared/hooks';

import NoteForConsultant from './NoteForConsultant';
import Schedule from './Schedule';

interface ConfirmationPageProps {
    offer: Offer;
}

/**
 * The pairs block is only worth showing when it adds something beyond the primary
 * role already shown as the title — i.e. there's a speciality, or more than one
 * distinct role. A lone role-only pair (the primary role) is hidden as redundant.
 */
function hasMeaningfulRoleSpecialtyPairs(pairs?: RoleSpecialtyOption[]): boolean {
    if (!pairs?.length) {
        return false;
    }

    const hasSpecialty = pairs.some(pair => pair.specialty);
    const distinctRoleIds = new Set(pairs.map(pair => pair.role?.id).filter(Boolean));

    return hasSpecialty || distinctRoleIds.size > 1;
}

export default function ConfirmationPage({ offer }: ConfirmationPageProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('candidateOffers');
    const isMobile = useIsMobile();
    const isRoleSpecialtyEnabled = useFeatureEnabled('RoleSpecialtyTaxonomy');

    const tenant = useTenantContext();

    const showRoleSpecialtyPairs = isRoleSpecialtyEnabled && hasMeaningfulRoleSpecialtyPairs(offer.roleSpecialtyPairs);

    const submitStatus = useSelector(submitStatusSelector);
    const declineOfferState = useSelector(declineOfferStateSelector);

    const isLoading = submitStatus === 'pending';

    useSetMobileHeader({
        title: offer?.role || t('mobileTitle'),
        backUrl: APP_ROUTES.candidateOffers
    });

    const handleOfferDecline = useCallback(() => {
        dispatch(openDeclineDialog({ offer }));
    }, [ dispatch, offer ]);

    const handleDeclineDialogClose = useCallback(() => {
        dispatch(closeDeclineDialog());
    }, [ dispatch ]);

    const handleDeclineDialogSubmit = useCallback<SubmitDeclineCallback>(async (offer) => {
        return dispatch(declineOfferThunk({ offer }));
    }, [ dispatch ]);

    const {
        openDialog: openAcceptOfferDialog,
        dialogState: acceptOfferDialogState
    } = useAcceptOfferDialogState(offer);

    const isAnyDialogOpen = acceptOfferDialogState.open || Boolean(declineOfferState);

    const controls = (
        <ActionButtonsGroup>
            <Button
                onClick={ handleOfferDecline }
                disabled={ isLoading }
                variant="outlined">
                { t('decline') }
            </Button>
            <Button
                onClick={ openAcceptOfferDialog }
                disabled={ isLoading || !acceptOfferDialogState.selectedItemsCount }
                variant="contained"
            >
                { t('apply') }
            </Button>
        </ActionButtonsGroup>
    );

    const content = (
        <Content divider={ <Divider /> }
            isMobile={ isMobile }
        >
            <OfferDescription { ...offer }
                timeZone={ tenant.timeZone }
                offerType={ offer.shifts?.length ? 'Shifts' : 'Weeks' }
                informationForCandidate={ offer.informationForCandidates }
                expandableInfoForCandidate
                role={ isMobile ? undefined : offer.role }
            />
            { showRoleSpecialtyPairs && (
                <Stack>
                    <Typography variant="subtitle2"
                        gutterBottom
                    >
                        { t('rolesSpecialties') }
                    </Typography>
                    <RoleSpecialtyChips value={ offer.roleSpecialtyPairs ?? [] } />
                </Stack>
            ) }
            <Schedule offer={ offer }
                timeZone={ tenant.timeZone }
            />
            <NoteForConsultant />
        </Content>
    );

    return (
        <FullPageContentCard>
            { !isMobile && (
                <PageContentHeading
                    title={ <ButtonBackToList listUri={ APP_ROUTES.candidateOffers } /> }
                    actions={ controls }
                />
            ) }

            { isMobile ? (
                <MobileColumn>
                    { content }
                    <StickyBottomSpacer />
                </MobileColumn>
            ) : content }

            { isMobile && !isAnyDialogOpen && (
                <StickyBottomActionContainer>
                    <FullWidthControls>
                        { controls }
                    </FullWidthControls>
                </StickyBottomActionContainer>
            ) }

            <DeclineOfferDialog dialogState={ declineOfferState }
                onCancel={ handleDeclineDialogClose }
                onConfirm={ handleDeclineDialogSubmit }
            />

            <AcceptOfferDialog { ...acceptOfferDialogState }
                customer={ offer.customer }
            />
        </FullPageContentCard>
    );
}

const Content = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isMobile'
})<{ isMobile: boolean }>(({ theme, isMobile }) => ({
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
    ...(isMobile && {
        flex: 1,
        minHeight: 0
    })
}));

const MobileColumn = styled('div')(({ theme }) => {
    const toolbarMinHeight = typeof theme.mixins.toolbar.minHeight === 'number'
        ? theme.mixins.toolbar.minHeight
        : 56;

    return {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: `calc(100dvh - ${ toolbarMinHeight }px - ${ theme.spacing(12) })`
    };
});

const StickyBottomSpacer = styled('div')(({ theme }) => ({
    height: theme.spacing(18),
    flexShrink: 0
}));

const FullWidthControls = styled('div')({
    width: '100%',

    '& > *': {
        width: '100%'
    }
});
